/* eslint-disable no-use-before-define */
/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable react/button-has-type */
import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { shape, bool, string } from 'prop-types';
import { isMobile } from 'react-device-detect';
import cn from 'classnames';

import { podcastShape, celebrityInfoShape } from 'constants/propTypesShapes';

import NarrativesControls from 'components/Players/Controls/NarrativesControls';
import { useDisappearingControls } from 'hooks';
import ActionableContents from 'components/actionableContents/ActionableContents';
import useVideoPlayer from './useVideoPlayer';
import './videojs.css';

const PodcastPlayer = React.memo(
  ({ CurrentIndex, podcast, celebrity, indexId, width, height, play, authenticated }) => {
    const { handleMouseMove, controlsVisible, handleClick } = useDisappearingControls(true);
    const playerRef = useRef(null);
    const [isBlurred, setIsBlurred] = useState(false);

    const applyBlur = useCallback(() => {
      if (videoRef.current) {
        videoRef.current.style.filter = 'blur(20px)';
      }
      if (playerRef.current) {
        playerRef.current.pause();
      }
      setIsBlurred(true);
    }, []);

    const removeBlur = useCallback(() => {
      if (videoRef.current) {
        videoRef.current.style.filter = 'none';
      }
      if (playerRef.current) {
        playerRef.current.play();
      }
      setIsBlurred(false);
    }, []);

    const handleBlur = useCallback(() => {
      applyBlur();
    }, [applyBlur]);

    const handleUserInteraction = useCallback(() => {
      if (isBlurred) {
        removeBlur();
      }
    }, [isBlurred, removeBlur]);

    useEffect(() => {
      window.addEventListener('blur', handleBlur);

      return () => {
        window.removeEventListener('blur', handleBlur);
      };
    }, [handleBlur]);

    const videoJsOptions = useMemo(
      () => ({
        autoplay: false,
        loop: false,
        controls: true,
        responsive: true,
        fluid: false,
        width,
        height,
        aspectRatio: isMobile ? '9:16' : '12:8',
        sources: [
          {
            src: podcast?.video.link,
            type: 'video/mp4'
          }
        ],
        tracks: {
          id: podcast?.id,
          type: 'Podcast',
          celebrity,
          title: podcast?.title
        }
      }),
      [podcast?.video.link, isMobile]
    );

    const onPlayerReady = useCallback(
      player => {
        console.log('Player is ready', player, podcast?.video.link);
        playerRef.current = player;
      },
      [podcast?.video.link]
    );

    const { videoRef } = useVideoPlayer({
      options: videoJsOptions,
      onReady: onPlayerReady,

      link: podcast?.video.link,
      mimeType: 'video/mp4'
    });

    return (
      <div
        className={cn(
          'video-container video-container-narratives video-ad-container-without-widget visible',
          {
            'no-pointer': isMobile
          }
        )}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        style={{
          position: 'relative',
          width: '100%'
        }}
      >
        <video
          ref={videoRef}
          width={width}
          height={height}
          // onClick={videoClicked}
          id={indexId}
          className="video-js vjs-default-skin"
        />
        {isBlurred && (
          <div className="blur-overlay" onClick={handleUserInteraction}>
            <p>Click to resume video playback</p>
          </div>
        )}

        {playerRef.current && play && (
          <>
            <NarrativesControls
              title={podcast?.title}
              description={podcast?.title}
              player={playerRef.current}
              celebrity={celebrity}
              narrativeId={podcast?.id}
              controlsVisible={controlsVisible}
              mute
              isPodcast
            />
            <ActionableContents
              videoTitle={podcast?.title}
              disableSharing={false}
              celebrity={celebrity}
              id={podcast?.id}
              authenticated={authenticated}
              contentType="Podcast"
            />
          </>
        )}
      </div>
    );
  }
);

PodcastPlayer.propTypes = {
  podcast: shape(podcastShape).isRequired,
  celebrity: shape(celebrityInfoShape),
  indexId: string.isRequired,
  play: bool
};

export default PodcastPlayer;
